import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, Briefcase, Wrench, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MySavedPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('TRADIE_LISTING');
  const [savedItems, setSavedItems] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeTab === 'LIKED_POSTS') {
      fetchLikedPosts();
    } else {
      fetchSavedItems();
    }
  }, [activeTab]);

  const fetchSavedItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/saved-items/my-saved?item_type=${activeTab}`);
      setSavedItems(response.data.saved_items || []);
    } catch (error) {
      console.error('Failed to fetch saved items:', error);
      toast.error('Failed to load saved items');
    } finally {
      setLoading(false);
    }
  };

  const fetchLikedPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/me/liked-posts`);
      setLikedPosts(response.data.posts || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (itemId) => {
    try {
      await axios.delete(`${API}/saved-items/unsave?item_type=${activeTab}&item_id=${itemId}`);
      toast.success('Item removed from saved');
      fetchSavedItems();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const renderTradieCard = (item) => {
    const tradie = item.item_data;
    const user = tradie.user;
    
    return (
      <Card key={item.item_id} className="hover:shadow-lg transition-shadow cursor-pointer">
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div 
              className="flex-1"
              onClick={() => navigate(`/tradies/${item.item_id}`)}
            >
              <h3 className="text-lg font-semibold text-foreground">{tradie.business_name}</h3>
              <p className="text-sm text-muted-foreground">{user?.full_name}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge>{tradie.tradie_type}</Badge>
                <Badge variant="outline">{tradie.location_suburb}, {tradie.location_state}</Badge>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleUnsave(item.item_id);
              }}
              className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
              title="Remove from saved"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{tradie.bio}</p>
          <p className="text-xs text-muted-foreground mt-2">Saved: {new Date(item.saved_at).toLocaleDateString()}</p>
        </CardContent>
      </Card>
    );
  };

  const renderJobCard = (item) => {
    const job = item.item_data;
    
    if (!job) return null;
    
    return (
      <Card key={item.item_id} className="hover:shadow-lg transition-shadow cursor-pointer">
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div 
              className="flex-1"
              onClick={() => navigate(`/jobs/${item.item_id}`)}
            >
              <h3 className="text-lg font-semibold text-foreground">{job.job_title}</h3>
              <p className="text-sm text-muted-foreground">{job.company_name}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge>{job.location_suburb}, {job.location_state}</Badge>
                {job.work_type && <Badge variant="outline">{job.work_type}</Badge>}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleUnsave(item.item_id);
              }}
              className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
              title="Remove from saved"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{job.description}</p>
          <p className="text-xs text-muted-foreground mt-2">Saved: {new Date(item.saved_at).toLocaleDateString()}</p>
        </CardContent>
      </Card>
    );
  };

  const renderLikedPostCard = (post) => {
    return (
      <Card key={post.id} className="hover:shadow-lg transition-shadow cursor-pointer">
        <CardContent className="p-4">
          <div 
            className="flex-1"
            onClick={() => navigate(`/community/${post.id}`)}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {post.author?.full_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">{post.author?.full_name || 'Unknown User'}</h3>
                <p className="text-xs text-muted-foreground">
                  {new Date(post.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-3">{post.content}</p>
            {post.image_url && (
              <img 
                src={post.image_url} 
                alt="Post" 
                className="w-full h-48 object-cover rounded-lg mb-3"
              />
            )}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                {post.likes_count || 0}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
          <Heart className="w-8 h-8 text-red-500" />
          My Saved Items
        </h1>
        <p className="text-muted-foreground">Items you&apos;ve bookmarked for later</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
        <button
          onClick={() => setActiveTab('TRADIE_LISTING')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'TRADIE_LISTING'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Wrench className="w-4 h-4" />
          Saved Tradies
        </button>
        <button
          onClick={() => setActiveTab('JOB')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'JOB'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Briefcase className="w-4 h-4" />
          Saved Jobs
        </button>
        <button
          onClick={() => setActiveTab('LIKED_POSTS')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'LIKED_POSTS'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Heart className="w-4 h-4" />
          Liked Posts
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      ) : activeTab === 'LIKED_POSTS' ? (
        likedPosts.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold text-foreground mb-2">No Liked Posts</h2>
            <p className="text-muted-foreground mb-6">
              You haven&apos;t liked any posts yet. Browse the community and like posts to see them here.
            </p>
            <button
              onClick={() => navigate('/community')}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Browse Community
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {likedPosts.map((post) => renderLikedPostCard(post))}
          </div>
        )
      ) : activeTab === 'LIKED_POSTS' ? (
        likedPosts.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold text-foreground mb-2">No Liked Posts</h2>
            <p className="text-muted-foreground mb-6">
              You haven&apos;t liked any posts yet. Browse the feed and like posts to see them here.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Browse Feed
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {likedPosts.map((post) => (
              <Card key={post.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/posts/${post.id}`)}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {post.author?.full_name?.[0] || 'U'}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{post.author?.full_name || 'User'}</h3>
                      <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <p className="text-sm mb-2 line-clamp-3">{post.text}</p>
                  <div className="flex items-center gap-1 text-sm text-red-500">
                    <Heart className="w-4 h-4 fill-red-500" />
                    <span>{post.like_count || 0}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : savedItems.length === 0 ? (
        <div className="text-center py-20">
          <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-semibold text-foreground mb-2">No Saved Items</h2>
          <p className="text-muted-foreground mb-6">
            {activeTab === 'TRADIE_LISTING' 
              ? "You haven&apos;t saved any tradies yet. Browse tradies and click the heart icon to save them."
              : "You haven&apos;t saved any jobs yet. Browse jobs and click the heart icon to save them."}
          </p>
          <button
            onClick={() => navigate(activeTab === 'TRADIE_LISTING' ? '/tradies' : '/jobs/browse')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Browse {activeTab === 'TRADIE_LISTING' ? 'Tradies' : 'Jobs'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {savedItems.map((item) => 
            activeTab === 'TRADIE_LISTING' ? renderTradieCard(item) : renderJobCard(item)
          )}
        </div>
      )}
    </div>
  );
};

export default MySavedPage;
